class TokenService {
  constructor() {
    this.tokenKey = "uwillToken";
    this.impersonationKey = "uwillImpersonation";
    this.sessionTimeoutKey = "uwill_session_timed_out";
  }

  // Store token with validation
  setToken(token) {
    if (token && this.isValidTokenFormat(token)) {
      localStorage.setItem(this.tokenKey, token);
      return true;
    }
    console.error("Invalid token format");
    return false;
  }

  // Get token with expiration check
  getToken() {
    let token = localStorage.getItem(this.tokenKey);

    // Treat explicit stringy "null"/"undefined"/empty as no token and remove them
    if (!token || token === "null" || token === "undefined" || token.trim() === "") {
      localStorage.removeItem(this.tokenKey);
      return null;
    }

    try {
      if (this.hasOldAlgorithm(token)) {
        console.log("Detected old token format, clearing for security update");
        this.clearAuth();
        return null;
      }

      if (this.isTokenExpired(token)) {
        this.clearAuth();
        return null;
      }
    } catch (error) {
      // Unexpected decode/validation error: remove bad token silently (avoid redirect loop)
      console.error("Token validation error:", error);
      localStorage.removeItem(this.tokenKey);
      return null;
    }

    return token;
  }

  // Check if token uses old "none" algorithm
  hasOldAlgorithm(token) {
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        // Decode header (first part)
        const header = JSON.parse(
          atob(parts[0].replace(/-/g, "+").replace(/_/g, "/"))
        );
        return header.alg === "none" || !header.alg;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  // Clear all auth data
  clearAuth(immediate = false) {
    // Prevent re-entrancy / repeated redirects
    if (sessionStorage.getItem("uwill_logout_in_progress") === "true") return;

    sessionStorage.setItem("uwill_logout_in_progress", "true");

    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.impersonationKey);

    // Only set the default timeout flash if no payload was previously persisted
    if (!sessionStorage.getItem("uwill_flash_payload")) {
      const defaultPayload = {
        message_type: "success",
        text: "Your session has timed out. Please log in again to continue."
      };
      sessionStorage.setItem("uwill_flash_payload", JSON.stringify(defaultPayload));
    }

    // Emit immediately only if not on login/logout (avoid double-emits/loops)
    const path = window.location.pathname;
    if (path !== "/logout" && path !== "/login") {
      if (!sessionStorage.getItem("uwill_flash_shown")) {
        const raw = sessionStorage.getItem("uwill_flash_payload");
        if (raw) {
          try {
            const payloadToEmit = JSON.parse(raw);
            window.Bus.$emit("flash-message", payloadToEmit);
            sessionStorage.setItem("uwill_flash_shown", "true");
          } catch (e) {
            console.error("failed to parse persisted flash payload", e);
          }
        }
      }
    }

    // Redirect logic unchanged
    if (immediate) {
      if (window.location.pathname !== "/logout") {
        window.location.replace("/logout");
      }
    } else {
      setTimeout(() => {
        if (window.location.pathname !== "/logout") {
          window.location.replace("/logout");
        }
      }, 1000);
    }
  }

  // Persist a flash payload for the next page load without redirecting/removing other flags.
  persistFlashPayload(flashPayload) {
    try {
      const payload = flashPayload || {
        message_type: "success",
        text: "You have successfully logged out."
      };
      sessionStorage.setItem("uwill_flash_payload", JSON.stringify(payload));
      // Allow the payload to be shown (clear any "shown" marker)
      sessionStorage.removeItem("uwill_flash_shown");
    } catch (e) {
      console.error("persistFlashPayload failed", e);
    }
  }

  // Validate JWT format (3 parts separated by dots)
  isValidTokenFormat(token) {
    const parts = token.split(".");
    return parts.length === 3;
  }

  // Check token expiration client-side
  isTokenExpired(token) {
    try {
      const payload = this.decodeToken(token);
      if (payload.exp) {
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        // Add 30 second buffer for clock skew
        return currentTime > expirationTime + 30000;
      }
      return false;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true;
    }
  }

  // Decode token payload (client-side only, no verification)
  decodeToken(token) {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid token format");
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(decoded);
    } catch (error) {
      console.error("Failed to decode token:", error);
      throw error;
    }
  }

  // Get user info from token
  getUserFromToken() {
    const token = this.getToken();
    if (token) {
      try {
        const payload = this.decodeToken(token);
        return {
          id: payload.id,
          email: payload.email
        };
      } catch (error) {
        return null;
      }
    }
    return null;
  }

  // Check if authenticated
  isAuthenticated() {
    const token = this.getToken();
    return token !== null && !this.isTokenExpired(token);
  }

  // Set impersonation flag
  setImpersonation(value) {
    if (value) {
      localStorage.setItem(this.impersonationKey, "true");
    } else {
      localStorage.removeItem(this.impersonationKey);
    }
  }

  // Set session timeout flag
  setSessionTimedOut(value) {
    if (value) {
      localStorage.setItem(this.sessionTimeoutKey, "true");
    } else {
      localStorage.removeItem(this.sessionTimeoutKey);
    }
  }
}

export default new TokenService();
